export const LAZY_RETRY_PARAM = '_lazy_retry';

interface FeatureRecoveryDeps {
  retriedOnLoad: boolean;
  currentUrl(): string;
  isOnline(): boolean;
  prepare(url: URL): Promise<boolean>;
  reload(url: URL): void;
  showRetry(retry: () => void): void;
}

type RecoveryState =
  | { status: 'available' | 'attempted' }
  | { status: 'preparing'; previous: 'available' | 'attempted' };

export function createFeatureRecovery(deps: FeatureRecoveryDeps) {
  let state: RecoveryState = { status: deps.retriedOnLoad ? 'attempted' : 'available' };
  let destination: string | undefined;
  const recover = async ({ resumeUrl, automatic = true }: { resumeUrl?: string; automatic?: boolean } = {}) => {
    destination = resumeUrl ?? destination;
    deps.showRetry(() => { void recover({ automatic: false }); });
    if (!deps.isOnline() || state.status === 'preparing' || (automatic && state.status === 'attempted')) return;
    const previous = state.status;
    state = { status: 'preparing', previous };
    try {
      const url = new URL(destination ?? deps.currentUrl());
      url.searchParams.set(LAZY_RETRY_PARAM, '1');
      const prepared = await deps.prepare(url);
      if (!deps.isOnline()) {
        state = { status: previous };
        return;
      }
      if (prepared) deps.reload(url);
    } catch (error) {
      console.error('Could not prepare feature recovery:', error);
    } finally {
      if (state.status === 'preparing') state = { status: 'attempted' };
    }
  };
  return { recover };
}
